/**
 * NewCourseGuideTab — Admin reference checklist for adding a new course.
 * Read-only. Shows all 16 steps with file paths, code examples, and priority badges.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronRight, BookOpen, CheckCircle2, AlertCircle, Info } from 'lucide-react';

// ─── Priority badge colours ────────────────────────────────────────────────────
const PRIORITY = {
  CRITICAL: { label: 'CRITICAL', cls: 'bg-rose-500/20 text-rose-300 border border-rose-500/40' },
  HIGH:     { label: 'HIGH',     cls: 'bg-amber-500/20 text-amber-300 border border-amber-500/40' },
  OPTIONAL: { label: 'OPTIONAL', cls: 'bg-slate-500/20 text-slate-400 border border-slate-500/30' },
};

// ─── All 16 steps ─────────────────────────────────────────────────────────────
const LAYERS = [
  {
    id: 'frontend-constants',
    label: 'Layer 1 — Frontend Constants',
    color: 'indigo',
    steps: [
      {
        n: 1,
        priority: 'CRITICAL',
        file: 'frontend/src/constants/courseData.js',
        section: 'COURSE_DATA object (lines ~10–305)',
        what: 'Add a new key for the course with all display + pricing metadata.',
        example: `NEWCOURSE: {
  code: 'NEWCOURSE',
  name: 'Short Name',
  fullName: 'Full Display Name',
  labs: 15,
  freeLabs: 2,
  price: 799,          // INR
  priceUSD: 35,        // USD
  icon: '🔧',
  color: 'blue',
  level: 'intermediate',
  description: 'What students will learn.',
  category: 'devops',  // devops | linux | programming | basic | database
  prerequisite: null,
  certification: null,
  isExamCourse: false,
  isProgrammingCourse: false,
  isDatabaseCourse: false,
}`,
      },
      {
        n: 2,
        priority: 'CRITICAL',
        file: 'frontend/src/constants/courseData.js',
        section: 'PRICING.categories (lines ~364–390)',
        what: "Add 'NEWCOURSE' to the correct pricing tier's courses array. If it's ₹799 DevOps tier, add it alongside SVRADM, DOCKER, etc.",
        example: `// Inside PRICING.categories, find the matching price bucket:
devops_tier: {
  price_inr: 799,
  price_usd: 35,
  courses: ['SVRADM', 'DOCKER', 'ANSIBLE', 'KUBERNETES', 'TERRAFORM', 'NEWCOURSE'],
}`,
      },
    ],
  },

  {
    id: 'backend-pricing',
    label: 'Layer 2 — Backend Pricing',
    color: 'amber',
    steps: [
      {
        n: 3,
        priority: 'CRITICAL',
        file: 'backend/app/routers/payments.py',
        section: 'COURSE_PRICING dict (lines ~50–82)',
        what: 'Add INR price in paise (multiply ₹ by 100).',
        example: `COURSE_PRICING = {
    # ... existing entries
    "NEWCOURSE": 79900,   # ₹799
}`,
      },
      {
        n: 4,
        priority: 'CRITICAL',
        file: 'backend/app/routers/payments.py',
        section: 'USD_PRICING dict (lines ~89–105)',
        what: 'Add USD price (whole dollars, Razorpay converts to cents internally).',
        example: `USD_PRICING = {
    # ... existing entries
    "NEWCOURSE": 35,   # $35
}`,
      },
    ],
  },

  {
    id: 'frontend-pages',
    label: 'Layer 3 — Frontend Pages',
    color: 'sky',
    steps: [
      {
        n: 5,
        priority: 'CRITICAL',
        file: 'frontend/src/pages/Courses.jsx',
        section: 'LOGO_URLS object (lines ~10–23) + courseMetadata (lines ~75–272)',
        what: 'Add CDN logo URL, then add a full metadata entry for the course card (title, description, tags, difficulty, prerequisites).',
        example: `// 1. Logo URL
const LOGO_URLS = {
  // ... existing
  NEWCOURSE: 'https://cdn.jsdelivr.net/gh/.../newcourse.svg',
};

// 2. courseMetadata entry
NEWCOURSE: {
  title: 'New Course Full Name',
  description: 'Short marketing blurb for the card.',
  tags: ['tag1', 'tag2'],
  difficulty: 'Intermediate',
  prerequisite: 'RH124',
}`,
      },
      {
        n: 6,
        priority: 'CRITICAL',
        file: 'frontend/src/pages/Dashboard.jsx',
        section: 'COURSE_LOGOS object (lines ~36–53)',
        what: "Add icon URL + Tailwind background colour so the course tile renders correctly on the student dashboard.",
        example: `COURSE_LOGOS = {
  // ... existing
  NEWCOURSE: {
    url: 'https://cdn.jsdelivr.net/gh/.../newcourse.svg',
    bg: 'bg-blue-500',
  },
}`,
      },
      {
        n: 7,
        priority: 'CRITICAL',
        file: 'frontend/src/pages/Pricing.jsx',
        section: 'getCourseLogo() (lines ~32–51) · getCourseGradient() (lines ~55–74) · learning path arrays (lines ~78–80)',
        what: 'Add logo mapping, gradient, and (if applicable) add the course to its learning path track.',
        example: `// In getCourseLogo():
'NEWCOURSE': LOGO_URLS.NEWCOURSE,

// In getCourseGradient():
'NEWCOURSE': 'from-blue-500 to-blue-600',

// In learning path array (if part of a track):
const DEVOPS_PATH = ['SVRADM', 'DOCKER', 'ANSIBLE', 'KUBERNETES', 'TERRAFORM', 'NEWCOURSE'];`,
      },
      {
        n: 8,
        priority: 'CRITICAL',
        file: 'frontend/src/pages/MockTests.jsx',
        section: 'COURSE_CONFIG object (lines ~35–200+)',
        what: 'Add config entry used for the course selector UI — required even if the course has no mock tests yet.',
        example: `NEWCOURSE: {
  name: 'NEWCOURSE',
  fullName: 'New Course Full Name',
  icon: SomeIcon,
  logo: LOGO_URLS.NEWCOURSE,
  color: 'from-blue-500 to-blue-600',
  bgColor: 'bg-blue-500',
}`,
      },
    ],
  },

  {
    id: 'backend-course-order',
    label: 'Layer 4 — Backend Course Ordering & Spec Loading',
    color: 'emerald',
    steps: [
      {
        n: 9,
        priority: 'CRITICAL',
        file: 'backend/app/routers/courses.py',
        section: 'course_order dict (lines ~26–40)',
        what: 'Assign the next available sort position so the course appears in the correct order in the catalog.',
        example: `course_order = {
    # ... existing
    "NEWCOURSE": 14,   # next available number
}`,
      },
      {
        n: 10,
        priority: 'CRITICAL',
        file: 'backend/app/routers/lab.py',
        section: 'course_order dict (lines ~63–73)',
        what: 'Same sort position as courses.py — this dict is intentionally duplicated here and both must stay in sync.',
        example: `course_order = {
    # ... existing
    "NEWCOURSE": 14,   # must match courses.py
}`,
      },
      {
        n: 11,
        priority: 'CRITICAL',
        file: 'backend/app/specs_loader.py',
        section: 'course_titles dict (lines ~119–135)',
        what: 'Map the course ID to its human-readable display name. The loader auto-scans the specs/ directory, but the title comes from this dict.',
        example: `course_titles = {
    # ... existing
    "NEWCOURSE": "New Course Full Display Name",
}`,
      },
    ],
  },

  {
    id: 'backend-access',
    label: 'Layer 5 — Backend Access Control',
    color: 'rose',
    steps: [
      {
        n: 12,
        priority: 'CRITICAL',
        file: 'backend/app/services/access_control.py',
        section: 'FREE_LABS dict (lines ~10–28) · fallback_counts dict (lines ~48–63 AND lines ~67–81)',
        what: 'Specify which labs are free-preview AND the total lab count. Note: fallback_counts appears twice in the file — update BOTH occurrences.',
        example: `# 1. Which labs are free (or empty list for fully paid):
FREE_LABS = {
    # ... existing
    "NEWCOURSE": ["lab1", "lab2"],
}

# 2. Total lab count — appears TWICE, update both:
fallback_counts = {
    # ... existing
    "NEWCOURSE": 15,
}`,
      },
    ],
  },

  {
    id: 'yaml-specs',
    label: 'Layer 6 — YAML Lab Specs (Content)',
    color: 'violet',
    steps: [
      {
        n: 13,
        priority: 'CRITICAL',
        file: 'backend/specs/NEWCOURSE/labs/lab1.yaml  …  labN.yaml',
        section: 'New directory + one YAML file per lab',
        what: 'Create the course folder and one YAML file per lab. Folder name must exactly match the course ID. The lab change detector auto-picks these up on next backend restart.',
        example: `# backend/specs/NEWCOURSE/labs/lab1.yaml
lab:
  id: "lab1"
  course: "NEWCOURSE"          # must match folder name exactly
  title: "Lab 1: Title Here"
  description: "What this lab covers."
  tasks:
    - id: "q1"
      title: "Task 1 Title"
      points: 20
      description: "What the student must do."
      hint: "Command or tip."
      instructions: "Step-by-step guide."
    - id: "q2"
      title: "Task 2 Title"
      points: 30
      description: "..."
      hint: "..."
      instructions: "..."

# Rules:
# • folder name = course field (case-sensitive)
# • lab IDs: lab1, lab2 … (lowercase, no leading zeros)
# • task IDs unique per lab: q1, q2, …
# • editing hint/description is SILENT (no student notification)
# • editing task TITLE triggers notification + re-attempt`,
      },
    ],
  },

  {
    id: 'pytest-tests',
    label: 'Layer 7 — Pytest Verification Tests',
    color: 'teal',
    steps: [
      {
        n: 14,
        priority: 'CRITICAL',
        file: 'backend/tests/NEWCOURSE/<lab_id>/test_q<n>.py',
        section: 'New directory tree + one test file per task',
        what: 'Create the test directory tree mirroring the YAML structure. Each file runs inside the student VM over SSH and asserts the task was completed. Called by /api/verify/.',
        example: `# Directory layout:
# backend/tests/NEWCOURSE/
#   lab1/
#     test_q1.py
#     test_q2.py
#   lab2/
#     test_q1.py

# Example: backend/tests/NEWCOURSE/lab1/test_q1.py
import pytest, subprocess

class TestLab1Q1:
    def test_task(self, vm_ip: str, student_user: str):
        result = subprocess.run(
            ["ssh", f"{student_user}@{vm_ip}", "some_verification_command"],
            capture_output=True, timeout=10,
        )
        assert result.returncode == 0, "Task not completed"`,
      },
    ],
  },

  {
    id: 'admin-docs',
    label: 'Layer 8 — Admin Panel Documentation',
    color: 'orange',
    steps: [
      {
        n: 15,
        priority: 'HIGH',
        file: 'frontend/src/components/admin/BlueprintTab.jsx',
        section: "BLUEPRINT[1].title — 'Courses & Labs (N labs · N courses)' string",
        what: 'Increment both the lab count and course count in the section title so the Blueprint tab stays accurate.',
        example: `// Before:
title: 'Courses & Labs (251 labs · 16 courses)',

// After (example: new course adds 15 labs):
title: 'Courses & Labs (266 labs · 17 courses)',`,
      },
    ],
  },

  {
    id: 'optional',
    label: 'Optional / Conditional Steps',
    color: 'slate',
    steps: [
      {
        n: 16,
        priority: 'OPTIONAL',
        file: 'Multiple files (see details)',
        section: 'Certificates · Mock Tests · Custom Email Templates',
        what: 'Only needed for specific course types. Skip these for a standard lab course.',
        example: `// A. If course has a CERTIFICATION exam:
// → frontend/src/components/admin/certificateTemplates.js
//   Add entry to CERTIFICATE_TEMPLATES with fullName, shortName,
//   code, vendor, color, tagline.

// B. If course gets MOCK TESTS:
// → backend/mock_test_questions_bank.py
//   Import new module + add to ALL_MOCK_TESTS + ALL_CATEGORIES.
// → Create: backend/mock_tests/newcourse_tests.py
//   with question bank structure.

// C. If course needs a CUSTOM ENROLLMENT EMAIL:
// → backend/app/templates/emails/newcourse_enrollment.html (Jinja2)
// → backend/app/services/email_service.py
//   Add send_newcourse_enrollment_email() method.

// ✅ NO changes needed in:
//   App.js (routes are dynamic)
//   models.py (all string-based, no enums)
//   main.py (specs auto-loaded at startup)
//   database_init.py (generic collections)
//   verify.py (generic access check)
//   analytics.py (queries generically)`,
      },
    ],
  },
];

// ─── Colour map ───────────────────────────────────────────────────────────────
const COLORS = {
  indigo:  { bg: 'bg-indigo-500/10',  border: 'border-indigo-500/20',  text: 'text-indigo-300',  iconBg: 'bg-indigo-500/20'  },
  amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/20',   text: 'text-amber-300',   iconBg: 'bg-amber-500/20'   },
  sky:     { bg: 'bg-sky-500/10',     border: 'border-sky-500/20',     text: 'text-sky-300',     iconBg: 'bg-sky-500/20'     },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-300', iconBg: 'bg-emerald-500/20' },
  rose:    { bg: 'bg-rose-500/10',    border: 'border-rose-500/20',    text: 'text-rose-300',    iconBg: 'bg-rose-500/20'    },
  violet:  { bg: 'bg-violet-500/10',  border: 'border-violet-500/20',  text: 'text-violet-300',  iconBg: 'bg-violet-500/20'  },
  teal:    { bg: 'bg-teal-500/10',    border: 'border-teal-500/20',    text: 'text-teal-300',    iconBg: 'bg-teal-500/20'    },
  orange:  { bg: 'bg-orange-500/10',  border: 'border-orange-500/20',  text: 'text-orange-300',  iconBg: 'bg-orange-500/20'  },
  slate:   { bg: 'bg-slate-500/10',   border: 'border-slate-500/20',   text: 'text-slate-400',   iconBg: 'bg-slate-500/20'   },
};

const TOTAL_STEPS = LAYERS.reduce((n, l) => n + l.steps.length, 0);
const CRITICAL_COUNT = LAYERS.flatMap(l => l.steps).filter(s => s.priority === 'CRITICAL').length;

// ─── Component ────────────────────────────────────────────────────────────────
export const NewCourseGuideTab = () => {
  const [expanded, setExpanded] = useState(() => new Set(LAYERS.map(l => l.id)));
  const [checked, setChecked] = useState(() => new Set());

  const toggle = (id) => setExpanded(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const toggleCheck = (n) => setChecked(prev => {
    const s = new Set(prev);
    s.has(n) ? s.delete(n) : s.add(n);
    return s;
  });

  const completedCritical = LAYERS
    .flatMap(l => l.steps)
    .filter(s => s.priority === 'CRITICAL' && checked.has(s.n))
    .length;

  const pct = Math.round((checked.size / TOTAL_STEPS) * 100);

  return (
    <div className="space-y-4" data-testid="new-course-guide-tab">

      {/* ── Header card ── */}
      <Card className="bg-gradient-to-br from-violet-500/10 to-indigo-500/10 border-violet-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-violet-300" />
            New Course Addition Guide
          </CardTitle>
          <p className="text-slate-400 text-sm">
            Complete checklist — every file that must be updated when adding a new course.
            Tick steps as you go; progress is session-scoped.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Steps',     value: TOTAL_STEPS,    color: 'text-violet-300' },
              { label: 'Critical',        value: CRITICAL_COUNT, color: 'text-rose-300'   },
              { label: 'Completed',       value: checked.size,   color: 'text-emerald-300'},
              { label: 'Critical Done',   value: `${completedCritical}/${CRITICAL_COUNT}`, color: 'text-amber-300' },
            ].map(s => (
              <div key={s.label} className="bg-white/5 rounded-lg p-3 border border-white/10 text-center">
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-slate-400 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Progress bar */}
          <div>
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Overall progress</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="text-slate-500">Priority:</span>
            {Object.values(PRIORITY).map(p => (
              <span key={p.label} className={`px-2 py-0.5 rounded-full font-semibold ${p.cls}`}>{p.label}</span>
            ))}
            <span className="text-slate-500 ml-2">Click step number to mark done.</span>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
            <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-rose-300">
              <span className="font-semibold">Sync rule:</span> Whenever you update <code className="bg-black/30 px-1 rounded">ALL_ACCESS_EXCLUDED</code> in{' '}
              <code className="bg-black/30 px-1 rounded">payments.py</code>, you MUST update the same set in{' '}
              <code className="bg-black/30 px-1 rounded">courseData.js</code> too — both must stay in lock-step.
            </p>
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg bg-sky-500/10 border border-sky-500/20">
            <Info className="w-4 h-4 text-sky-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-sky-300">
              Editing a lab task's <span className="font-semibold">hint or description</span> is silent — no student notification, no score reset.
              Editing a task's <span className="font-semibold">title</span> triggers a notification and forces re-attempt.
              Fix bad verifications via test-file edits only (never title edits).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ── Layer sections ── */}
      {LAYERS.map(layer => {
        const c = COLORS[layer.color] || COLORS.slate;
        const isOpen = expanded.has(layer.id);
        const layerDone = layer.steps.filter(s => checked.has(s.n)).length;

        return (
          <Card key={layer.id} className={`${c.bg} ${c.border} backdrop-blur`}>
            <button
              onClick={() => toggle(layer.id)}
              className="w-full text-left"
              data-testid={`ncg-layer-${layer.id}`}
            >
              <CardHeader className="flex flex-row items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.iconBg} text-sm font-bold ${c.text}`}>
                    {layerDone === layer.steps.length
                      ? <CheckCircle2 className="w-4 h-4" />
                      : layer.steps[0].n}
                  </div>
                  <div>
                    <CardTitle className="text-white text-base">{layer.label}</CardTitle>
                    <p className={`text-xs ${c.text} opacity-80`}>
                      {layer.steps.length} step{layer.steps.length !== 1 ? 's' : ''} · {layerDone}/{layer.steps.length} done
                    </p>
                  </div>
                </div>
                {isOpen
                  ? <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  : <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />}
              </CardHeader>
            </button>

            {isOpen && (
              <CardContent className="space-y-4 pt-0">
                {layer.steps.map(step => {
                  const p = PRIORITY[step.priority];
                  const done = checked.has(step.n);
                  return (
                    <div
                      key={step.n}
                      data-testid={`ncg-step-${step.n}`}
                      className={`rounded-lg border p-4 space-y-3 transition-all duration-200 ${
                        done
                          ? 'border-emerald-500/30 bg-emerald-500/5'
                          : 'border-white/10 bg-white/5 hover:bg-white/[0.07]'
                      }`}
                    >
                      {/* Step header */}
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => toggleCheck(step.n)}
                          title="Mark as done"
                          className={`flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${
                            done
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : 'border-slate-600 text-slate-400 hover:border-slate-400'
                          }`}
                        >
                          {done ? <CheckCircle2 className="w-4 h-4" /> : step.n}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge className={`text-[10px] font-bold px-2 py-0 ${p.cls}`}>{p.label}</Badge>
                            {done && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">✓ Done</Badge>}
                          </div>
                          <code className="text-violet-300 text-xs font-mono break-all">{step.file}</code>
                          <p className={`text-[11px] mt-0.5 ${c.text} opacity-70`}>{step.section}</p>
                        </div>
                      </div>

                      {/* What to do */}
                      <p className="text-slate-300 text-sm leading-relaxed pl-10">{step.what}</p>

                      {/* Code example */}
                      <div className="pl-10">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Example</div>
                        <pre className="text-xs text-slate-300 font-mono bg-black/40 rounded-lg px-4 py-3 overflow-x-auto border border-white/5 leading-relaxed whitespace-pre-wrap break-words">
                          {step.example}
                        </pre>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* ── Final validation checklist ── */}
      <Card className="bg-emerald-500/10 border-emerald-500/20 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            Pre-Launch Validation
          </CardTitle>
          <p className="text-xs text-slate-400">Run through these after all 16 steps are done.</p>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            {[
              'YAML lab files are valid — no duplicate task IDs, course field matches folder name.',
              'Each lab has corresponding pytest test files in backend/tests/NEWCOURSE/.',
              'Course appears in GET /api/courses endpoint response.',
              'Course can be purchased via POST /api/payments/create-order.',
              'Enrolled user can access labs via GET /api/lab/list/{course}.',
              'Labs verify successfully via POST /api/verify/{course}/{lab_id}.',
              'Progress is tracked in GET /api/progress/my.',
              'All Access bundle includes / excludes course correctly.',
              'Frontend Pricing page shows new course with correct price.',
              'Frontend Dashboard shows course tile with logo and colour.',
              'FREE_LABS for this course are accessible without enrollment.',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 p-2 rounded-lg bg-white/5 border border-white/5 text-slate-300">
                <span className="text-emerald-500 text-xs font-mono flex-shrink-0 mt-0.5">{String(i + 1).padStart(2, '0')}.</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewCourseGuideTab;
